import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { Roll } from '@app/models/knitting/roll';
import { KnittingOrderRoles } from '@app/models/roles/roles-group';
import { EnumService } from '@app/services/enum.service';
import { KnittingOrder, KnittingOrderSearchCommand } from '../../../models/production/knitting-order';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';

@Component({
  selector: 'app-kniting-order',
  templateUrl: './knitting-order.component.html',
  styleUrl: './knitting-order.component.scss',
})
export class KnittingOrderComponent implements OnInit {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private knittingorderProxyService = inject(KnittingOrderProxyService);
  private enumService = inject(EnumService);

  // Signals
  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  knittingorders = signal<KnittingOrder[]>([]);
  readonly filterState = createDynamicFilterState({ storageKey: 'knitting-orders-filters', config: this.filterConfig });
  showFilters = signal(false);
  canCreate = signal(false);
  showRollBreakdown = signal(false);
  expandedRows = signal<Record<string, boolean>>({});
  // Track roll weight filter per knitting order: 'all' | 'with-weight' | 'without-weight'
  rollWeightFilters = signal<Record<string, string>>({});

  statusOptions: EnumValues = {};
  private filtersPanelInitialized = false;

  // Pagination
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  private currentSearchCriteria: KnittingOrderSearchCommand = {};

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    this.statusOptions = this.enumService.getEnum('KnittingOrderStatus', 'productionEnums')?.values ?? {};
  }

  redirectToDetails(id?: string) {
    this.router.navigate(['/knitting-order-details'], { queryParams: { id: id ?? 'new' } });
  }

  ngOnInit(): void {
    this.canCreateKnittingOrder();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    if (!this.statusOptions || Object.keys(this.statusOptions).length === 0) {
      this.statusOptions = this.enumService.getEnum('KnittingOrderStatus', 'productionEnums')?.values ?? {};
    }

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'knittingOrderNumber',
          label: translate('Order_Number', 'Order Number'),
          placeholder: translate('Enter_order_number', 'Enter order number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Status', 'Status'),
          placeholder: translate('Select_Status', 'Select Status'),
          options: this.statusOptions,
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  getPlannedQuantity(ko: KnittingOrder): number {
    return ko.fabricQuantityDetails.reduce((sum, detail) => sum + (detail.quantity || 0), 0);
  }

  getDistinctLotNumbers(ko: KnittingOrder): string {
    const lotNumbers = new Set<string>();
    ko.fabricQuantityDetails.forEach((detail) => {
      detail.yarnAndProviderInfos.forEach((info) => {
        if (info.providerLotNumber) {
          lotNumbers.add(info.providerLotNumber);
        }
      });
    });
    return Array.from(lotNumbers).join(', ');
  }

  getProgressPercentage(ko: KnittingOrder): number {
    const planned = this.getPlannedQuantity(ko);
    const issued = ko.issuedQuantity || 0;

    if (planned === 0) return 0;

    const percentage = (issued / planned) * 100;
    return Math.min(Math.floor(percentage), 100);
  }

  getActualProgressPercentage(ko: KnittingOrder): number {
    const planned = this.getPlannedQuantity(ko);
    const issued = ko.issuedQuantity || 0;

    if (planned === 0) return 0;
    return (issued / planned) * 100;
  }

  getProgressBarClass(ko: KnittingOrder): string {
    const percentage = this.getActualProgressPercentage(ko);

    if (percentage < 95) {
      return 'cs-progressbar-severity-low';
    } else if (percentage >= 95 && percentage < 100) {
      return 'cs-progressbar-severity-medium';
    } else if (percentage >= 100 && percentage <= 105) {
      return 'cs-progressbar-severity-high';
    } else {
      return 'cs-progressbar-severity-complete cs-progressbar-severity-over';
    }
  }

  getProgressTextColor(ko: KnittingOrder): string {
    const percentage = this.getActualProgressPercentage(ko);

    if (percentage < 95) {
      return '#10b981';
    } else if (percentage >= 95 && percentage < 100) {
      return '#f59e0b';
    } else if (percentage >= 100 && percentage <= 105) {
      return '#f97316';
    } else {
      return '#ef4444';
    }
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const getStatuses = (value: unknown): number[] | undefined => {
      if (!value) return undefined;
      if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'value' in value[0]) {
          return value.map((item: { value: number }) => item.value);
        }
        return value as number[];
      }
      return undefined;
    };

    const searchCriteria: KnittingOrderSearchCommand = {
      knittingOrderNumber: (filters['knittingOrderNumber'] as string) || undefined,
      statuses: getStatuses(filters['statuses']),
    };

    this.currentSearchCriteria = searchCriteria;
    this.currentPage.set(1);
    this.searchKnittingOrders(searchCriteria);
  }

  searchKnittingOrders(criteria?: KnittingOrderSearchCommand) {
    this.startLoading();

    const searchCriteria = criteria || {};

    this.knittingorderProxyService
      .searchKnittingOrder(
        searchCriteria,
        this.currentPage(),
        this.pageSize(),
        this.showRollBreakdown(),
        this.sortField(),
        this.sortOrder(),
      )
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.knittingorders.set(response.data.items);
            this.totalRecords.set(response.data.totalCount);
          } else {
            this.knittingorders.set([]);
            this.totalRecords.set(0);
          }
        },
        error: (error) => {
          console.error('Error fetching knitting orders:', error);
          this.knittingorders.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  toggleRollBreakdown(): void {
    this.showRollBreakdown.update((val) => !val);
    this.expandedRows.set({});
    this.searchKnittingOrders(this.currentSearchCriteria);
  }

  toggleRowExpansion(ko: KnittingOrder): void {
    if (!ko.id) return;
    this.expandedRows.update((rows) => {
      const newRows = { ...rows };
      if (newRows[ko.id!]) {
        delete newRows[ko.id!];
      } else {
        newRows[ko.id!] = true;
      }
      return newRows;
    });
  }

  isRowExpanded(ko: KnittingOrder): boolean {
    return ko.id ? (this.expandedRows()[ko.id] ?? false) : false;
  }

  getRollWeightInLbs(roll: Roll): number {
    if (!roll.stationTimeStamps || roll.stationTimeStamps.length === 0) {
      return 0;
    }
    const latestTimestamp = roll.stationTimeStamps[roll.stationTimeStamps.length - 1];
    return latestTimestamp?.weightInLbs || 0;
  }

  getFabricDescription(ko: KnittingOrder, fabricCode: string): string {
    const fabric = ko.fabrics?.find((f) => f.fabricCode === fabricCode);
    return fabric?.longDescription || fabricCode;
  }

  getRollWeightFilter(ko: KnittingOrder): string {
    return ko.id ? (this.rollWeightFilters()[ko.id] ?? 'all') : 'all';
  }

  setRollWeightFilter(ko: KnittingOrder, filter: string): void {
    if (!ko.id) return;
    this.rollWeightFilters.update((filters) => ({
      ...filters,
      [ko.id!]: filter,
    }));
  }

  getFilteredRolls(ko: KnittingOrder): Roll[] {
    if (!ko.rolls) return [];
    const filter = this.getRollWeightFilter(ko);
    switch (filter) {
      case 'with-weight':
        return ko.rolls.filter((roll) => this.getRollWeightInLbs(roll) > 0);
      case 'without-weight':
        return ko.rolls.filter((roll) => this.getRollWeightInLbs(roll) === 0);
      default:
        return ko.rolls;
    }
  }

  getRollsWithWeightCount(ko: KnittingOrder): number {
    if (!ko.rolls) return 0;
    return ko.rolls.filter((roll) => this.getRollWeightInLbs(roll) > 0).length;
  }

  getRollsWithoutWeightCount(ko: KnittingOrder): number {
    if (!ko.rolls) return 0;
    return ko.rolls.filter((roll) => this.getRollWeightInLbs(roll) === 0).length;
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.searchKnittingOrders(this.currentSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.searchKnittingOrders(this.currentSearchCriteria);
  }

  private canCreateKnittingOrder() {
    if (AuthService.hasRole(KnittingOrderRoles.KnittingOrderAllEditor)) {
      this.canCreate.set(true);
    }
  }
}
